import { Component, Input, Inject, OnDestroy, OnInit, EventEmitter, Output, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import moment from 'moment';
import { Moment } from 'moment';
import { catchError, debounceTime, map, startWith, takeUntil, tap } from 'rxjs/operators';
import { OrderShuttleVM } from '../../../../../models/orderModel';
import { ArrivalVM, DepartureVM } from '../../../../../models/ArrivalDepartureModel';
import { PlannedShuttleBuss } from '../../../../../models/ShuttleBookingModel';
import { ArrivalDepartureService } from '../../../../../services/arrivaldeparture.service';
import { OrderShuttleService } from '../../../../../services/ordershuttle.service';
import { ShuttelbussService } from '../../../../../services/shuttlebuss.service';
import { DatePipe } from '@angular/common';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { format } from 'date-fns';

@Component({
  selector: 'app-back-office24-order-shuttle-edit',
  templateUrl: './back-office24-order-shuttle-edit.component.html',
  styleUrl: './back-office24-order-shuttle-edit.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})

export class BackOffice24OrderShuttleEditComponent implements OnInit, OnDestroy {
  @Input() orderShuttleId: string;
  @Output() saveChangesEvent = new EventEmitter<void>();
  orderShuttleVM: OrderShuttleVM;
  editOrderShuttleVM: OrderShuttleVM;
  arrivalVM: ArrivalVM;
  departureVM: DepartureVM;
  currentArrival: ArrivalVM;
  currentDeparture: DepartureVM;
  departures: DepartureVM[] = [];
  arrivals: ArrivalVM[] = [];
  terminalOptions: { value: string; name: string }[] = [];
  statusOptions: { value: string; name: string }[] = [
    { value: 'New', name: 'New' },
    { value: 'Paid', name: 'Active' },
    { value: 'ReadyForPickup', name: 'Ready for pickup' },
    { value: 'Boarded', name: 'Boarded' },
    { value: 'DroppedOff', name: 'DroppedOff' },
    { value: 'NoShow', name: 'No show' },
    { value: 'Cancelled', name: 'Cancelled' }
  ];

  isDisabled = true;
  bookingType: string = 'flight';
  editShuttleForm: UntypedFormGroup;
  shuttleDateTime: string;
  plannedShuttleBusses: PlannedShuttleBuss[] = [];
  currentShuttleBuss: PlannedShuttleBuss;
  componentDestroyed$: Subject<boolean> = new Subject();
  filteredDepartures: Observable<DepartureVM[]>;
  filteredArrivals: Observable<ArrivalVM[]>;
  isLoading = false;
  selectedFlightStationId: string = '5ac25e00-7863-4b8c-9fe1-eb8af7c7e77e';
  activeRequests: number = 0;
  arrivalFlightShuttleTimeDiff: number = 0;

  constructor(
    @Inject(MAT_DIALOG_DATA) public params: any,
    private fb: UntypedFormBuilder,
    public snackBar: MatSnackBar,
    public orderShuttleService: OrderShuttleService,
    public shuttleBussService: ShuttelbussService,
    public arrivalDepartureService: ArrivalDepartureService,
    public datepipe: DatePipe
  ) { }

  ngOnInit(): void {

    if (this.params.orderShuttleId) {
      this.orderShuttleId = this.params.orderShuttleId;
    }

    this.initEmptyShuttleForm();
    this.getTerminalOptions();
    this.getOrderShuttle(this.orderShuttleId);
  }

  getTerminalOptions() {
    this.arrivalDepartureService
      .getTerminals('59D90114-9889-474E-A8A8-D1926F0DEE70')
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        terminals => {
          this.terminalOptions = (terminals || []).map(t => ({
            value: t.terminalName,
            name: t.terminalName
          }));
          // this.ensureSelectedTerminalOption();
        },
        error => {
          console.error('Error fetching terminals:', error);
        }
      );
  }

  // ensureSelectedTerminalOption() {
  //   const currentTerminal = this.orderShuttleVM?.terminal || this.editShuttleForm?.get('terminal')?.value;
  //   if (currentTerminal && !this.terminalOptions.some(opt => opt.value === currentTerminal)) {
  //     this.terminalOptions.push({ value: currentTerminal, name: currentTerminal });
  //   }
  // }

  private setLoadingState(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  private incrementRequests() {
    this.activeRequests++;
    this.setLoadingState(true);
  }

  private decrementRequests() {
    if (this.activeRequests > 0)
      this.activeRequests--;
    if (this.activeRequests === 0) {
      this.setLoadingState(false);
    }
  }

  private _filterDepartures(departureCode: string): DepartureVM[] {
    const filterValue = departureCode.toLowerCase();
    return this.departures.filter(departure => this.displayAutocompleteFn(departure).toLowerCase().includes(filterValue));
  }

  private _filterArrivals(arrivalCode: string): ArrivalVM[] {
    const filterValue = arrivalCode.toLowerCase();
    return this.arrivals.filter(arrival => this.displayAutocompleteFn(arrival).toLowerCase().includes(filterValue));
  }

  flightVMValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value && !(value.arrivalCode) && !(value.departureCode)) {
        return { invalidFlight: true };
      }
      return null;
    };
  }

  displayAutocompleteFn(FlightDetails: DepartureVM | ArrivalVM): string {
    if (!FlightDetails) return '';

    if ((FlightDetails as ArrivalVM).arrivalCode) {
      const value = (FlightDetails as ArrivalVM);
      const formattedTime = format(new Date(value.scheduledArrival), 'HH:mm');
      return `${value.arrivalCode} ${value.departureStation} : ${formattedTime} ${value.stationCode} : ${value.terminalName}`;
    } else if ((FlightDetails as DepartureVM).departureCode) {
      const value = (FlightDetails as DepartureVM);
      const formattedTime = format(new Date(value.scheduledDeparture), 'HH:mm');
      return `${value.departureCode} ${value.arrivalStation} : ${formattedTime} ${value.stationCode} : ${value.terminalName}`;
    }
  }

  initEmptyShuttleForm() {
    this.editShuttleForm = this.fb.group({
      orderShuttleId: [''],
      status: [''],
      flightNr: [''],
      terminal: [''],
      passengers: [''],
      childSeats: [''],
      baggage: [''],
      comment: [''],
      shuttleDate: [''],
      shuttleDateTime: [''],
      timeTableDate: [''],
      timeTableTime: [''],
      flightDate: [''],
      flightArrivalTime: [''],
      flightDepartureTime: [''],
      orderShuttleBusId: [''],
      departureId: [''],
      arrivalId: [''],
    });
  }

  initEditShuttleForm() {
    this.bookingType = this.determineInitialBookingType();

    this.editShuttleForm = this.fb.group({
      orderShuttleId: [this.orderShuttleVM?.orderShuttleId],
      status: [this.orderShuttleVM?.status],
      flightNr: [this.orderShuttleVM?.flightNr],
      terminal: [this.orderShuttleVM?.terminal],
      passengers: [this.orderShuttleVM?.passengers],
      childSeats: [this.orderShuttleVM?.childSeats],
      baggage: [this.orderShuttleVM?.baggage],
      comment: [this.orderShuttleVM?.comment],
      shuttleDate: [this.orderShuttleVM?.shuttleDate, Validators.required],
      shuttleDateTime: [this.shuttleDateTime, Validators.required],
      timeTableDate: [this.orderShuttleVM?.shuttleDate],
      timeTableTime: [this.shuttleDateTime],
      flightDate: [this.orderShuttleVM?.arrivalVM ? this.orderShuttleVM?.arrivalVM.scheduledArrival : this.orderShuttleVM?.departureVM ? this.orderShuttleVM?.departureVM.scheduledDeparture : ''],
      flightArrivalTime: [this.shuttleDateTime],
      flightDepartureTime: [this.shuttleDateTime],
      orderShuttleBusId: [this.orderShuttleVM?.orderShuttleBusId],
      departureId: [this.orderShuttleVM?.departureId],
      arrivalId: [this.orderShuttleVM?.arrivalId],
    });

    const listChange = this.editShuttleForm.valueChanges;
    listChange
      .pipe(debounceTime(1000))
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((change: any) => {
        const formControls = this.editShuttleForm.controls;
        const hasChanges = Object.keys(change).some(key => formControls[key].dirty && formControls[key].value !== this.orderShuttleVM[key]);

        if (!this.isLoading && hasChanges) {
          this.editOrderShuttleVM = change;
          this.isDisabled = false;
          this.snackBar.open(`Du har gjort ändringar, glöm inte att spara`, 'Tack!', {
            verticalPosition: 'top',
            duration: 2000
          });
        }
      });

    this.editShuttleForm.get('flightArrivalTime').valueChanges.pipe(
      startWith(''),
      map(value => {
        if (value?.arrivalCode && value.arrivalId !== this.orderShuttleVM?.arrivalVM?.arrivalId) {
          this.calculateShuttleDateAndTimeForArrival(
            value.scheduledArrival,
            this.editShuttleForm.controls['baggage'].value
          );
          this.arrivalVM = value;
        }
        if (value?.terminalName) {
          this.editShuttleForm.controls['terminal'].setValue(value.terminalName, { emitEvent: false });
        }
        return typeof value === 'string' ? value : value?.arrivalCode;
      }),
      map(arrivalCode => arrivalCode ? this._filterArrivals(arrivalCode) : this.arrivals.slice())
    ).subscribe(filteredArrivals => {
      this.filteredArrivals = new Observable<ArrivalVM[]>(observer => {
        observer.next(filteredArrivals);
        observer.complete();
      });
    });

    this.editShuttleForm.get('baggage').valueChanges
      .subscribe(baggage => {
        if (this.editShuttleForm.controls['flightArrivalTime'].value.arrivalCode
          && typeof baggage === 'number'
          && this.orderShuttleVM?.arrivalVM.arrivalId !== this.editShuttleForm.controls['flightArrivalTime'].value.arrivalId
        ) {
          this.calculateShuttleDateAndTimeForArrival(
            this.editShuttleForm.controls['flightArrivalTime'].value.scheduledArrival,
            baggage
          );
        }
      });

    this.editShuttleForm.get('flightDepartureTime').valueChanges.pipe(
      startWith(''),
      map(value => {
        if (value?.departureCode && value.departureId !== this.orderShuttleVM?.departureVM?.departureId) {
          this.calculateShuttleDateAndTimeForDeparture(value.scheduledDeparture);
          this.departureVM = value;
        }
        if (value?.terminalName) {
          this.editShuttleForm.controls['terminal'].setValue(value.terminalName, { emitEvent: false });
        }
        return typeof value === 'string' ? value : value?.departureCode;
      }),
      map(departureCode => departureCode ? this._filterDepartures(departureCode) : this.departures.slice())
    ).subscribe(filteredDepartures => {
      this.filteredDepartures = new Observable<DepartureVM[]>(observer => {
        observer.next(filteredDepartures);
        observer.complete();
      });
    });
  }

  isFlightBookingMode(): boolean {
    return this.bookingType === 'flight';
  }

  getShuttleDateType(): string {
    if (this.editShuttleForm) {
      if (this.isFlightBookingMode()) {
        return 'FLIGHT';
      }

      if (this.orderShuttleVM?.orderShuttleBusId != null) {
        return 'TIMETABLE';
      }

      return 'DEFAULT';
    }

    if (this.orderShuttleVM) {
      if (this.orderShuttleVM?.arrivalId != null || this.orderShuttleVM?.departureId != null) {
        return 'FLIGHT';
      }

      if (this.orderShuttleVM?.orderShuttleBusId != null) {
        return 'TIMETABLE';
      }
    }

    return 'DEFAULT';
  }

  getOrderShuttle(orderShuttleId: string) {
    this.incrementRequests();
    this.orderShuttleService
      .getOrderShuttle(orderShuttleId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(orderShuttleVM => {
        this.setOrderShuttle(orderShuttleVM);
        this.initEditShuttleForm();

        if (this.isFlightBookingMode() && this.orderShuttleVM?.departureId) {
          this.getDeparture(this.orderShuttleVM?.departureId);
        }

        if (this.isFlightBookingMode() && this.orderShuttleVM?.arrivalId) {
          this.getArrival(this.orderShuttleVM?.arrivalId);
        }

        if (this.getShuttleDateType() === 'TIMETABLE') {
          this.getPlannedShuttleBusses(moment(this.orderShuttleVM?.shuttleDate).format('YYYY-MM-DD'), moment(this.orderShuttleVM?.shuttleDate).add(1, 'd').format('YYYY-MM-DD'));
        }

        this.decrementRequests();
      }, () => {
        this.decrementRequests();
      });
  }

  private getActiveArrivalContext(): ArrivalVM | undefined {
    return this.arrivalVM ?? this.orderShuttleVM?.arrivalVM ?? this.currentArrival ?? undefined;
  }

  private getActiveDepartureContext(): DepartureVM | undefined {
    return this.departureVM ?? this.orderShuttleVM?.departureVM ?? this.currentDeparture ?? undefined;
  }

  private resolveFlightStationId(preferredStationId?: string): Observable<string | null> {
    if (preferredStationId) {
      this.selectedFlightStationId = preferredStationId;
      return of(preferredStationId);
    }

    if (this.selectedFlightStationId) {
      return of(this.selectedFlightStationId);
    }

    const activeArrivalStationId = this.getActiveArrivalContext()?.stationId;
    if (activeArrivalStationId) {
      this.selectedFlightStationId = activeArrivalStationId;
      return of(activeArrivalStationId);
    }

    const activeDepartureStationId = this.getActiveDepartureContext()?.stationId;
    if (activeDepartureStationId) {
      this.selectedFlightStationId = activeDepartureStationId;
      return of(activeDepartureStationId);
    }

    const organizationId = this.orderShuttleVM?.organizationId || this.orderShuttleVM?.['shuttleOrganizationId'];
    if (organizationId) {
      return this.arrivalDepartureService.getStations(organizationId).pipe(
        map(stations => {
          const stationId = stations?.[0]?.stationId ?? null;
          if (stationId) {
            this.selectedFlightStationId = stationId;
          }
          return stationId;
        }),
        catchError(() => of(null))
      );
    }

    return of(null);
  }

  getArrival(arrivalId: string) {
    this.incrementRequests();
    this.arrivalDepartureService
      .getArrival(arrivalId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(arrival => {
        this.arrivalVM = arrival;

        if (this.getShuttleDateType() === 'FLIGHT') {
          const activeArrival = this.getActiveArrivalContext();
          const activeArrivalDate = activeArrival?.scheduledArrival;
          this.resolveFlightStationId(activeArrival?.stationId).subscribe(stationId => {
            if (stationId && activeArrivalDate) {
              this.getArrivals(
                moment(activeArrivalDate).format('YYYY-MM-DD'),
                moment(activeArrivalDate).add(1, 'd').format('YYYY-MM-DD'),
                () => {
                  this.setFormFlightArrivalTime();
                },
                stationId
              );
            }
          });
        }
        this.decrementRequests();
      }, () => {
        this.decrementRequests();
      });
  }

  getDeparture(departureId: string) {
    this.incrementRequests();
    this.arrivalDepartureService
      .getDeparture(departureId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(departure => {
        this.departureVM = departure;

        if (this.getShuttleDateType() === 'FLIGHT') {
          const activeDeparture = this.getActiveDepartureContext();
          const activeDepartureDate = activeDeparture?.scheduledDeparture;
          this.resolveFlightStationId(activeDeparture?.stationId).subscribe(stationId => {
            if (stationId && activeDepartureDate) {
              this.getDepartures(
                moment(activeDepartureDate).format('YYYY-MM-DD'),
                moment(activeDepartureDate).add(1, 'd').format('YYYY-MM-DD'),
                () => {
                  this.setFormFlightDepartureTime();
                },
                stationId
              );
            }
          });
        }
        this.decrementRequests();
      }, () => {
        this.decrementRequests();
      });
  }

  getPlannedShuttleBusses(startDate: string, endDate: string) {
    this.incrementRequests();
    this.plannedShuttleBusses = [];
    this.shuttleBussService
      .getPlannedOrderShuttleBusType(this.orderShuttleVM?.routeCode, startDate, endDate, this.orderShuttleVM?.shuttleTypeCode)
      .subscribe(plannedShuttleBusses => {
        const now = new Date();
        const futureTime = new Date(now.getTime() + 20 * 60 * 1000);
        const formatter = new Intl.DateTimeFormat('sv-SE', {
          timeZone: 'Europe/Stockholm',
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        });
        const parts = formatter.formatToParts(futureTime);
        const partsMap = parts.reduce((acc, part) => {
          acc[part.type] = part.value;
          return acc;
        }, {} as any);
        const currentSwedishISO = `${partsMap.year}-${partsMap.month}-${partsMap.day}T${partsMap.hour}:${partsMap.minute}:${partsMap.second}`;

        plannedShuttleBusses.forEach((shuttle: any) => {
          shuttle.startDate = moment(shuttle.startDate);
          const shuttleISO = shuttle.startDate._i.slice(0, 19);

          if (shuttleISO > currentSwedishISO) {
            this.plannedShuttleBusses.push(shuttle);
          }
          if (shuttle.orderShuttleBusId == this.orderShuttleVM?.orderShuttleBusId) {
            this.currentShuttleBuss = shuttle;
          }
        });

        if (this.currentShuttleBuss) {
          this.editShuttleForm.controls['timeTableTime'].setValue(this.currentShuttleBuss, { emitEvent: false });
        }
        this.decrementRequests();
      }, () => {
        this.decrementRequests();
      });
  }

  async getDepartures(startDate: string, endDate: string, callback?: () => void, stationId?: string) {
    this.incrementRequests();
    this.departures = [];
    try {
      const activeStationId = stationId ?? this.selectedFlightStationId ?? this.getActiveDepartureContext()?.stationId;
      if (!activeStationId) {
        this.filteredDepartures = of([]);
        this.decrementRequests();
        if (callback) {
          callback();
        }
        return;
      }

      const departures = await this.arrivalDepartureService.getDeparturesStationBetweenDepartureDate(activeStationId, startDate, endDate).toPromise();
      this.departures = departures ?? [];
      this.departures.forEach(departure => {
        if (departure.departureId == this.orderShuttleVM?.departureId) {
          this.currentDeparture = departure;
        }
      });

      this.filteredDepartures = of(this.departures.slice());
    } catch (error) {
      console.error('Error fetching departures:', error);
      this.filteredDepartures = of([]);
    } finally {
      this.decrementRequests();
      if (callback) {
        callback();
      }
    }
  }

  calculateShuttleDateAndTimeForDeparture(flightDepartureTime) {
    const shuttleTime = moment(flightDepartureTime).subtract(3, 'hours');
    this.editShuttleForm.controls['shuttleDate'].patchValue(shuttleTime.toDate(), { emitEvent: false });
    this.editShuttleForm.controls['shuttleDateTime'].patchValue(shuttleTime.format('HH:mm'), { emitEvent: false });
  }

  calculateShuttleDateAndTimeForArrival(arrivalTime, baggage: number) {
    arrivalTime = moment(arrivalTime);
    let shuttleTime;
    shuttleTime = baggage > 0 ? arrivalTime.add(45, 'minutes') : arrivalTime.add(25, 'minutes');

    this.editShuttleForm.controls['shuttleDate'].patchValue(shuttleTime.toDate(), { emitEvent: false });
    this.editShuttleForm.controls['shuttleDateTime'].patchValue(shuttleTime.format("HH:mm"), { emitEvent: false });
  }

  setFormFlightDepartureTime() {
    if (this.currentDeparture) {
      const departureTime = this.editShuttleForm.controls['flightDepartureTime'];
      departureTime.setValue(this.currentDeparture, { emitEvent: true });
      departureTime.setValidators([Validators.required, this.flightVMValidator()]);
      departureTime.updateValueAndValidity();
    }
  }

  async getArrivals(startDate: string, endDate: string, callback?: () => void, stationId?: string) {
    this.incrementRequests();
    this.arrivals = [];
    try {
      const activeStationId = stationId ?? this.selectedFlightStationId ?? this.getActiveArrivalContext()?.stationId;
      if (!activeStationId) {
        this.filteredArrivals = of([]);
        this.decrementRequests();
        if (callback) {
          callback();
        }
        return;
      }

      const arrivals = await this.arrivalDepartureService.getArrivalsStationBetweenArrivalDate(activeStationId, startDate, endDate).toPromise();
      this.arrivals = arrivals ?? [];
      this.arrivals.forEach(arrival => {
        if (arrival.arrivalId == this.orderShuttleVM?.arrivalId) {
          this.currentArrival = arrival;
        }
      });
      this.filteredArrivals = of(this.arrivals.slice());
    } catch (error) {
      console.error('Error fetching arrivals:', error);
      this.filteredArrivals = of([]);
    } finally {
      this.decrementRequests();
      if (callback) {
        callback();
      }
    }
  }

  setFormFlightArrivalTime() {
    if (this.currentArrival) {
      const arrivalTime = this.editShuttleForm.controls['flightArrivalTime'];
      arrivalTime.setValue(this.currentArrival, { emitEvent: true });
      arrivalTime.setValidators([Validators.required, this.flightVMValidator()]);
      arrivalTime.updateValueAndValidity();
    }
  }

  saveChanges() {
    this.incrementRequests();
    this.setSaveData();
    const editShuttleObservable = this.orderShuttleService.editOrderShuttle(this.editShuttleForm.value).pipe(
      takeUntil(this.componentDestroyed$),
      catchError(error => {
        this.snackBar.open(`Felkod: ${error.status}`, 'Doh!', {
          verticalPosition: 'top'
        });
        throw error;
      })
    );

    forkJoin([editShuttleObservable]).subscribe(([orderShuttleVM]) => {
      this.snackBar.open('Du har sparat ändringarna', 'OK', {
        verticalPosition: 'top',
        duration: 2000
      });
      this.getOrderShuttle(orderShuttleVM?.orderShuttleId);
      this.isDisabled = true;
      this.decrementRequests();
      this.saveChangesEvent.emit();
    },
      error => {
        this.decrementRequests();
        this.snackBar.open(`Felkod: ${error.status}`, 'Doh!', {
          verticalPosition: 'top'
        });
      });

    this.initEmptyShuttleForm();
  }

  timeTableDateChanged(event: MatDatepickerInputEvent<Date>) {
    const selectedDate: Moment = moment(event.value);
    const selectedStartDate = selectedDate.format('YYYY-MM-DD');
    const endDate = selectedDate.add(1, 'd').format('YYYY-MM-DD');
    this.getPlannedShuttleBusses(selectedStartDate, endDate);
  }

  flightDateChanged(event: MatDatepickerInputEvent<Date>) {
    const selectedDate: Moment = moment(event.value);
    const selectedStartDate = selectedDate.format('YYYY-MM-DD');
    const endDate = selectedDate.add(1, 'd').format('YYYY-MM-DD');

    if (!this.isFlightBookingMode()) {
      return;
    }

    this.editShuttleForm.patchValue({
      flightArrivalTime: '',
      flightDepartureTime: '',
      shuttleDate: '',
      shuttleDateTime: ''
    }, { emitEvent: false });

    this.resolveFlightStationId(this.getActiveArrivalContext()?.stationId).subscribe(stationId => {
      if (stationId) {
        this.getArrivals(selectedStartDate, endDate, () => {
          this.editShuttleForm.controls['flightArrivalTime'].markAsTouched();
        }, stationId);
      }
    });

    this.resolveFlightStationId(this.getActiveDepartureContext()?.stationId).subscribe(stationId => {
      if (stationId) {
        this.getDepartures(selectedStartDate, endDate, () => {
          this.editShuttleForm.controls['flightDepartureTime'].markAsTouched();
        }, stationId);
      }
    });
  }

  setOrderShuttle(orderShuttleVM: OrderShuttleVM) {
    this.orderShuttleVM = orderShuttleVM;
    this.shuttleDateTime = this.datepipe.transform(this.orderShuttleVM?.shuttleDate, 'HH:mm');
    // this.ensureSelectedTerminalOption();
  }

  setSaveData() {
    this.setDateFormats();
    this.editShuttleForm.patchValue({
      status: this.getStatus()
    }, { emitEvent: false });

    if (this.getShuttleDateType() == 'TIMETABLE') {
      this.editShuttleForm.patchValue({
        shuttleDate: moment(this.editShuttleForm.controls['timeTableDate'].value).format('YYYY-MM-DD') + ' ' + this.editShuttleForm.controls['timeTableTime'].value.startDate.format("HH:mm"),
        shuttleDateTime: this.editShuttleForm.controls['timeTableTime'].value.startDate.format("HH:mm"),
        orderShuttleBusId: this.editShuttleForm.controls['timeTableTime'].value.orderShuttleBusId
      }, { emitEvent: false });
    }

    if (this.bookingType === 'flight' && this.getShuttleDateType() == 'FLIGHT') {
      const selectedArrival = this.editShuttleForm.controls['flightArrivalTime'].value;
      const selectedDeparture = this.editShuttleForm.controls['flightDepartureTime'].value;

      this.editShuttleForm.patchValue({
        orderShuttleBusId: this.orderShuttleVM?.orderShuttleBusId ?? null,
        arrivalId: selectedArrival?.arrivalId ?? null,
        departureId: selectedDeparture?.departureId ?? null,
        flightNr: selectedArrival?.arrivalCode ?? selectedDeparture?.departureCode ?? this.orderShuttleVM?.flightNr
      }, { emitEvent: false });
    }
  }

  setDateFormats() {
    try {
      const shuttleDateVal = this.editShuttleForm.controls['shuttleDate'].value;
      const shuttleTimeVal = this.editShuttleForm.controls['shuttleDateTime'].value;
      const dateStr = moment(shuttleDateVal).format('YYYY-MM-DD');
      this.editShuttleForm.patchValue({
        shuttleDate: dateStr + 'T' + shuttleTimeVal
      });
    } catch (err) {
      console.error(err);
    }
  }

  getStatus() {
    if (this.editShuttleForm.controls['status'].value == 'New') {
      return 1;
    }
    if (this.editShuttleForm.controls['status'].value == 'Paid') {
      return 2;
    }
    if (this.editShuttleForm.controls['status'].value == 'ReadyForPickup') {
      return 4;
    }
    if (this.editShuttleForm.controls['status'].value == 'Boarded') {
      return 8;
    }
    if (this.editShuttleForm.controls['status'].value == 'DroppedOff') {
      return 16;
    }
    if (this.editShuttleForm.controls['status'].value == 'NoShow') {
      return 32;
    }
    if (this.editShuttleForm.controls['status'].value == 'Cancelled') {
      return 64;
    }
  }

  determineInitialBookingType(): any {
    // if (this.orderShuttleVM?.arrivalId == null && this.orderShuttleVM?.departureId == null && this.orderShuttleVM?.orderShuttleBusId != null) {
    //   return null;
    // }

    if (this.orderShuttleVM?.arrivalId != null || this.orderShuttleVM?.departureId != null) {
      return 'flight';
    }
    return 'hotel';
  }

  onBookingTypeChange(bookingType: string): void {
    this.bookingType = bookingType;
    this.isDisabled = false;
    if (bookingType === 'flight') {
      this.editShuttleForm.patchValue({
        timeTableDate: null,
        timeTableTime: null,
        orderShuttleBusId: this.orderShuttleVM?.orderShuttleBusId ?? null,
      }, { emitEvent: false });

      this.currentShuttleBuss = null;
      this.plannedShuttleBusses = [];

    } else if (bookingType === 'hotel') {
      const wasFlightBooking = this.orderShuttleVM?.arrivalId != null || this.orderShuttleVM?.departureId != null;
      this.editShuttleForm.patchValue({
        flightNr: null,
        flightDate: null,
        flightArrivalTime: null,
        flightDepartureTime: null,
        arrivalId: null,
        departureId: null,
      }, { emitEvent: false });
      this.editShuttleForm.get('flightNr').clearValidators();
      this.editShuttleForm.get('flightNr').updateValueAndValidity({ emitEvent: false });
      this.editShuttleForm.get('flightArrivalTime').clearValidators();
      this.editShuttleForm.get('flightArrivalTime').updateValueAndValidity({ emitEvent: false });
      this.editShuttleForm.get('flightDepartureTime').clearValidators();
      this.editShuttleForm.get('flightDepartureTime').updateValueAndValidity({ emitEvent: false });

      // Clear flight data
      this.currentArrival = null;
      this.currentDeparture = null;
      this.arrivals = [];
      this.departures = [];
    }
  }

  ngOnDestroy() {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
}
